<template>
  <div class="mb-4">
    <p class="lead">Choose a free license.</p>
    <small class="d-block mb-4">If you haven't licensed your project yet, choose the license you will be using.</small>
    <div class="card-columns mb-4">
      <div class="card">
        <div class="card-body btn-group-toggle" :class="{'bg-primary text-white': license !== null && (license.substr(0, 3) === 'gpl' || license.substr(1, 3) === 'gpl')}">
          <h5 class="card-title">GPL</h5>
          <label class="btn btn-outline-primary" :class="{'disabled': license !== null && (license.substr(0, 3) === 'gpl' || license.substr(1, 3) === 'gpl')}">
            <input type="radio" v-model="license" value="gpl" />
            Select
          </label>
        </div>
      </div>
      <div class="card">
        <div class="card-body btn-group-toggle" :class="{'bg-primary text-white': license !== null && license.substr(0, 2) === 'cc'}">
          <h5 class="card-title">Creative Commons</h5>
          <label class="btn btn-outline-primary" :class="{'disabled': license !== null && license.substr(0, 2) === 'cc'}">
            <input type="radio" v-model="license" value="cc" />
            Select
          </label>
        </div>
      </div>
      <div class="card">
        <div class="card-body btn-group-toggle" :class="{'bg-primary text-white': license === 'other'}">
          <h5 class="card-title">Other</h5>
          <label class="btn btn-outline-primary" :class="{'disabled': license === 'other'}">
            <input type="radio" v-model="license" value="other" />
            Select
          </label>
        </div>
      </div>
    </div>
    <small class="d-block mb-4">Not sure? We recommend GPLv3 (chaotic good) or Creative Commons (neutral good).</small>
    <div class="card" v-show="license === 'mit'">
      <div class="card-body">
        <h5 class="card-title">Currently on MIT</h5>
        <p>We love MIT! It's the true spirit of openness.</p>
        <p>
          Unfortunately, it's so open that it requires a lot of legal duct tape to dual-license alongside a paid license.
        </p>
        <p>
          The key component of dual-licensing is telling the user "you choose whichever of these licenses you want". If
          a big company can choose MIT they will, and there will be no point in providing a paid license.
        </p>
        <p>
          We recommend GPLv3 for anyone on MIT looking to dual license. On its own it has its downsides, and its viral
          component is a turn-off if you're a free spirit.
        </p>
        <p>
          But when used as one component of a dual-license setup, it really does shine. The setup becomes stupidly
          simple. Ironically, getting that kind of lubrication on MIT&mdash;a three-sentence file&mdash;is just not
          possible without hiring lawyers.
        </p>
      </div>
    </div>
    <div class="card" v-show="license !== null && (license.substr(0, 3) === 'gpl' || license.substr(1, 3) === 'gpl')">
      <div class="card-body">
        <h5 class="card-title">GPL</h5>
        <p>Great! Mind telling us which version?</p>
        <div class="btn-group-toggle mb-4">
          <div class="btn-group mr-4">
            <label class="btn btn-outline-primary" :class="{active: license === 'gplv2'}">
              <input type="radio" v-model="license" value="gplv2" />
              GPLv2
            </label>
            <label class="btn btn-outline-primary" :class="{active: license === 'gplv3'}">
              <input type="radio" v-model="license" value="gplv3" />
              GPLv3
            </label>
          </div>
          <div class="btn-group mr-4">
            <label class="btn btn-outline-primary" :class="{active: license === 'lgpl'}">
              <input type="radio" v-model="license" value="lgpl" />
              LGPL
            </label>
          </div>
          <div class="btn-group">
            <label class="btn btn-outline-primary" :class="{active: license === 'agpl'}">
              <input type="radio" v-model="license" value="agpl" />
              AGPL
            </label>
          </div>
        </div>
        <div v-show="license === 'gplv2'">
          <p>Awesome! GPLv2 works fine for a dual-license setup.</p>
          <p>
            Your users will have the option of fulfilling the terms of the GPL (primarily that their work must also be
            licensed as GPLv2), or of paying for a commercial license.
          </p>
        </div>
        <div v-show="license === 'gplv3'">
          <p>Great! GPLv3 slides nicely into a simple dual-license setup.</p>
          <p>
            Your users will have the option of fulfilling the terms of the GPL (primarily that their work must also be
            licensed as GPLv3), or of paying for a commercial license.
          </p>
        </div>
        <div v-show="license === 'lgpl'">
          <p>
            Sorry, but we don't support LGPL currently. We recommend GPLv3 for dual-licensing. If that doesn't work for
            you <a href="mailto:hi@license.land">contact us</a> so we can learn more about your situation!
          </p>
        </div>
        <div v-show="license === 'agpl'">
          <p>
            Sorry, but we don't support AGPL currently. We recommend GPLv3 for dual-licensing. If that doesn't work for
            you <a href="mailto:hi@license.land">contact us</a> so we can learn more about your situation!
          </p>
        </div>
      </div>
    </div>
    <div class="card" v-show="license !== null && license.substr(0, 2) === 'cc'">
      <div class="card-body">
        <h5 class="card-title">Creative Commons</h5>
        <p>Great! Want to allow adaptations to be shared?</p>
        <div class="btn-group-toggle mb-4">
          <div class="btn-group mr-4">
            <label class="btn btn-outline-primary" :class="{active: license === 'cc-by-nc'}">
              <input type="radio" v-model="license" value="cc-by-nc" />
              Yes
            </label>
            <label class="btn btn-outline-primary" :class="{active: license === 'cc-by-nc-nd'}">
              <input type="radio" v-model="license" value="cc-by-nc-nd" />
              No
            </label>
            <label class="btn btn-outline-primary" :class="{active: license === 'cc-by-nc-sa'}">
              <input type="radio" v-model="license" value="cc-by-nc-sa" />
              If they share alike
            </label>
          </div>
        </div>
        <small class="d-block mb-4">
          Check <a href="https://creativecommons.org/choose/">creativecommons.org</a> for details.
        </small>
        <div v-show="license !== null && license.substr(0, 8) === 'cc-by-nc'">
          <p>Okay! This is great for a dual-license setup.</p>
          <p>
            Your users will have the option of fulfilling the terms of your Creative Commons license (primarily that it
            must be for noncommercial use), or of paying for your commercial license.
          </p>
        </div>
      </div>
    </div>
    <div class="card" v-show="license === 'other'">
      <div class="card-body">
        <h5 class="card-title">Something else</h5>
        <p>We <i class="fas fa-heart" /> all open source licenses!</p>
        <p>
          For simplicity we support GPL and Creative Commons as free licenses. We love MIT (and many more!) but they
          don't fit well into a dual-license setup.
        </p>
        <p>
          The key component of this setup is telling users "you choose whichever license you want". If a big company can
          choose MIT, they will.
        </p>
        <p>
          Therefore your free license's terms should naturally guide big companies towards your paid license. It can do
          this by restricting commercial use (like Creative Commons) or requiring the consuming software to be open
          (like GPL).
        </p>
        <p>
          Please <a href="mailto:hi@license.land">contact us</a> if you have any feedback!
        </p>
      </div>
    </div>
  </div>
</template>

<script>
const scrollDown = function() {
  window.setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 50)
}

export default {
  name: 'CurrentLicense',
  data: () => ({
    license: null,
  }),
  props: ['repo', 'value'],
  watch: {
    license: async function() {
      scrollDown()
      if (this.license !== null && this.license.substr(0, 4) === 'gplv' || this.license.substr(0, 5) === 'cc-by') {
        this.$emit('input', this.license)
        scrollDown()
        await fetch(`${process.env.VUE_APP_FIREBASE_FUNCTIONS_BASE_URL}/license?license=${this.license}&repo=${this.repo}`, {
          credentials: 'include',
          method: 'POST'
        })
      } else {
        this.$emit('input', null)
      }
    },
  },
}
</script>

<style>
html {
  scroll-behavior: smooth;
}
</style>
